import { useTheme } from '@emotion/react'
import {
    BreachEvent,
    DataLayerEventName,
    ViewSubscriptionItem,
} from '@news-mono/web-common'
import { UseQueryResult } from '@tanstack/react-query'
import {
    GetOffersResult,
    OfferVariantType,
} from '@west-australian-newspapers/subscription-offer-types'
import React, { useEffect, useMemo } from 'react'
import { getAttribution } from '../../../../errors/ErrorPage'
import { IconExclamation } from '../../../../icons'
import { DEFAULT_CART_QUANTITY, toItemListId } from '../../../../tracking'
import { getOffersApiPackageDetails, SubscriptionPackages } from '../../common'
import {
    mapSubscriptionPackagesToFlatList,
    RibbonOptions,
    SubscriptionPackageButtonStyle,
    SubscriptionPackageOptions,
    SubscriptionPackageTheme,
    SubscriptionPackageType,
} from '../../helpers/BreachWallTypes'
import { AflSubscriptionPackage } from '../AflSubscriptionPackage/AflSubscriptionPackage'
import { SubscriptionPackageRedesign2026 } from '../SubscriptionPackageRedesign2026/SubscriptionPackageRedesign2026'
import {
    BreachWallWrapper,
    StyledBreachWallContent2022,
    StyledErrorContainer,
    StyledErrorIconContainer,
    StyledFooterAndTermsContainer,
    StyledIconSpinner,
    StyledPackagesContainer,
    StyledRegistrationInfo,
} from './BreachWallContent.styled'

const FOOTY_LIVES_HERE_MOBILE_IMAGE = require('../../assets/thewest-digital-hero-variation-footy-lives-here-mobile.jpg')
const FOOTY_LIVES_HERE_DESKTOP_IMAGE = require('../../assets/thewest-digital-hero-variation-footy-lives-here.jpg')
const WEST_LOGO_GRADIENT = require('../../assets/thewest-w-gradient.png')

export interface BreachWallContentProps {
    title: () => JSX.Element | null
    terms: () => JSX.Element | null
    footer: () => JSX.Element | null
    mediaBanner: () => JSX.Element | null
    breachBanner: () => JSX.Element | null
    offerResult: UseQueryResult<GetOffersResult>
    // Values passed down from the BreachWallContentWrapper in relation to the breach
    breachCreativeName: string
    breachType: OfferVariantType
    breachDeal: string
    onEvent: (event: BreachEvent) => void
    // Values passed down if it's a test BreachWallContent
    isTest: boolean | undefined
    isRegionalOverride: boolean
}

export interface SubscriptionPackageProps {
    breachCreativeName: string
    breachDeal: string
    breachType: OfferVariantType
    packageInfo: SubscriptionPackageOptions
    masthead?: string
    packageTheme?: SubscriptionPackageTheme
    cancellationMessage?: string
    googleSKU?: string
    onEvent: (event: BreachEvent) => void
    isRegionalOverride: boolean
    isSwgPage?: boolean
    desiredRedirect?: string
}

export interface PackageOptions {
    theme: SubscriptionPackageTheme
    type?: SubscriptionPackageType
    gridColumnPosition?: SubscriptionPackageOptions['columnPosition']
    isRecommended?: boolean
}
export interface BreachOptions {
    breachType?: OfferVariantType
    useBreachDealStyle?: boolean
    isRegional?: boolean
}
export interface PackageThemeProps {
    packageOptions: PackageOptions
    breachOptions: BreachOptions
    buttonStyle?: SubscriptionPackageButtonStyle
    ribbonType?: RibbonOptions
}

export const BreachWallContent: React.FC<BreachWallContentProps> = ({
    mediaBanner: renderMediaBanner,
    breachBanner: renderBanner,
    terms: renderTerms,
    title: renderTitleSection,
    footer: renderFooter,
    offerResult,
    breachCreativeName,
    breachType,
    breachDeal,
    onEvent,
    isRegionalOverride,
}) => {
    const subscriptionPackages: SubscriptionPackages | undefined =
        useMemo(() => {
            if (!offerResult.isSuccess) {
                return
            }
            return getOffersApiPackageDetails(
                'breach',
                breachType,
                offerResult.data,
            )
        }, [breachType, offerResult.data])

    const theme = useTheme()
    const masthead =
        getAttribution(theme.kind, theme.section) || 'The West Australian'
    // Assign background image if utilising new breach design.
    const packageImages = useMemo(() => {
        let largeImage = undefined
        let smallImage = undefined

        switch (breachType) {
            case 'afl-footy-lives-here-breach-screen':
                largeImage = FOOTY_LIVES_HERE_DESKTOP_IMAGE
                smallImage = FOOTY_LIVES_HERE_MOBILE_IMAGE
                break
            case '50-off-campaign':
                largeImage = WEST_LOGO_GRADIENT
                break
        }

        return {
            largeImage,
            smallImage,
        }
    }, [breachType])

    const fireAnalyticsEvent = () => {
        const availableDeal = breachDeal === '' ? 'default' : breachDeal
        const breachAvailablePayload: BreachEvent['payload'] = {
            paywallType: 'freemium',
            leadDepth: 0,
            creative: breachCreativeName,
            deal: availableDeal,
            breachScreenType: 'Premium - Inline',
            popupMethod: 'auto',
            pricepoint: [7, 8],
        }
        onEvent({
            type: DataLayerEventName.breachScreenAvailable,
            originator: 'breachscreen',
            payload: breachAvailablePayload,
        })

        const flatSubscriptions = subscriptionPackages
            ? mapSubscriptionPackagesToFlatList(subscriptionPackages)
            : undefined
        const viewSubscriptionItems: ViewSubscriptionItem[] =
            flatSubscriptions?.map((pkg, index) => ({
                index,
                deal_name: breachType,
                item_name: pkg.heading,
                item_id: pkg.rateCode,
                item_brand: masthead,
                component_path: pkg.packagePath,
                item_list_category: 'freemium',
                item_list_category2: 'Premium - Inline',
                item_list_name: breachCreativeName,
                item_list_id: toItemListId(breachCreativeName),
                price: pkg.weeklyPrice,
                promo_price: pkg.promoPrice || 0,
                discount: pkg.discount || 0,
                coupon: pkg.offerCode || '',
                quantity: pkg.quantity || DEFAULT_CART_QUANTITY,
                currency: 'AUD',
            })) || []

        onEvent({
            type: DataLayerEventName.viewSubscriptionItemListEvent,
            parentType: DataLayerEventName.viewItemListEvent,
            originator: 'breachscreen',
            payload: { items: viewSubscriptionItems },
        })
    }
    useEffect(() => {
        fireAnalyticsEvent()
    }, [])

    const fullWidthBelowDesktopBreaches: OfferVariantType[] = [
        'afl-footy-lives-here-breach-screen',
        '50-off-campaign',
    ]
    const isFullWidthBelowDesktop =
        fullWidthBelowDesktopBreaches.includes(breachType)

    return (
        <BreachWallWrapper
            isFullWidthBelowDesktop={isFullWidthBelowDesktop}
            breachType={breachType}
        >
            <StyledBreachWallContent2022
                backgroundColor={'#F4F5F5'}
                largeImage={packageImages.largeImage}
                smallImage={packageImages.smallImage}
                breachType={breachType}
            >
                {/* NOTE: DO NOT HARD CODE ANYTHING HERE UNLESS ABSOLUTELY NECESSARY, USE THE PROP RENDER METHODS */}
                {renderMediaBanner()}
                {['default', 'digital-intro-default'].includes(breachType) ? (
                    renderTitleSection()
                ) : (
                    <StyledRegistrationInfo breachType={breachType}>
                        {breachType && renderTitleSection()}
                    </StyledRegistrationInfo>
                )}
                {offerResult.isError ? (
                    <StyledErrorContainer>
                        <StyledErrorIconContainer>
                            <IconExclamation />
                        </StyledErrorIconContainer>
                        <span>
                            <h2>We couldn’t load your offer</h2>
                            <p>Please try again.</p>
                        </span>
                        <button onProgress={() => offerResult.refetch()}>
                            Retry
                        </button>
                    </StyledErrorContainer>
                ) : offerResult.isLoading ? (
                    <StyledIconSpinner />
                ) : (
                    // NOTE: DO NOT HARD CODE ANYTHING HERE UNLESS ABSOLUTELY NECESSARY, USE THE PROP RENDER METHODS
                    <StyledPackagesContainer
                        breachType={breachType}
                        numberOfColumns={subscriptionPackages?.length}
                        useGridLayout={canUseGridLayout(breachType)}
                    >
                        {subscriptionPackages &&
                            subscriptionPackages.map(
                                (subscriptionPackageItem, index) => {
                                    const theme =
                                        subscriptionPackageItem.packageTheme

                                    const SubscriptionPackageComponent =
                                        theme === 'afl-footy-design-2026'
                                            ? AflSubscriptionPackage
                                            : SubscriptionPackageRedesign2026

                                    return (
                                        <SubscriptionPackageComponent
                                            key={index}
                                            breachCreativeName={
                                                breachCreativeName
                                            }
                                            breachDeal={breachDeal}
                                            breachType={breachType}
                                            onEvent={onEvent}
                                            packageInfo={
                                                subscriptionPackageItem
                                            }
                                            masthead={masthead}
                                            isRegionalOverride={
                                                isRegionalOverride
                                            }
                                        />
                                    )
                                },
                            )}
                    </StyledPackagesContainer>
                )}
                {/* NOTE: DO NOT HARD CODE ANYTHING HERE UNLESS ABSOLUTELY NECESSARY, USE THE PROP RENDER METHODS */}
                <StyledFooterAndTermsContainer breachType={breachType}>
                    {renderFooter()}
                    {renderTerms()}
                    {renderBanner()}
                </StyledFooterAndTermsContainer>
            </StyledBreachWallContent2022>
        </BreachWallWrapper>
    )
}

function canUseGridLayout(breachType: OfferVariantType) {
    const gridLayoutTypes: OfferVariantType[] = [
        'afl-footy-lives-here-breach-screen',
    ]
    return gridLayoutTypes.includes(breachType)
}

BreachWallContent.displayName = 'BreachWallContent'
export default BreachWallContent
